import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'

import { DrawerWrapper } from '@/components'
import { useAnalyticsHeaderSlot } from '@/features/Analytics/components/AnalyticsHeaderSlot/useAnalyticsHeaderSlot'
import { BenchmarkingActions } from '@/features/Analytics/components/BenchmarkingActions/BenchmarkingActions'
import { shouldDisplayAreaSetting } from '@/features/Analytics/components/BenchmarkinAreaSetting/shouldDisplayAreaSetting'
import { useAnalyticsFiltersStore } from '@/features/Analytics/stores/useAnalyticsFiltersStore/useAnalyticsFiltersStore'
import useAnalyticsStore from '@/features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import { useAnalyticsFiltersGetParams } from '@/features/Analytics/hooks/useAnalyticsFiltersGetParams'
import { BenchmarkingRequestParams, useElementWidth } from '@/hooks'
import { BenchmarkingDrawer } from './BenchmarkingDrawer'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { getPreviousYearTimeline } from '@/components/common/Filter/TimelineFilter/utils'
import { BenchmarkingReport } from '@/features/Analytics/components/BenchmarkingReport/BenchmarkingReport'

export const Benchmarking: React.FC = () => {
    const { setSlot } = useAnalyticsHeaderSlot()
    const [isOpen, setIsOpen] = useState(false)
    const toggleButtonRef = React.useRef<HTMLButtonElement>(null)
    const toggleWidth = useElementWidth(toggleButtonRef)
    const activeFilters = useAnalyticsFiltersStore(
        (state) => state.activeFilters
    )

    const filtersParams = useAnalyticsFiltersGetParams(activeFilters)

    const reportSettings = useAnalyticsStore(
        (state) => state.benchmarking.reportSettings
    )

    const setReportSettings = useAnalyticsStore(
        (state) => state.benchmarking.setReportSettings
    )

    const baseYearStart = useAuthStore(
        (state) => state.customer?.configs.baseYearStart
    )
    const baseYearEnd = useAuthStore(
        (state) => state.customer?.configs.baseYearEnd
    )

    const baselineYearTimeline =
        baseYearStart && baseYearEnd
            ? {
                  startDate: baseYearStart,
                  endDate: baseYearEnd,
              }
            : undefined

    const previousYearTimeline = useMemo(() => {
        return getPreviousYearTimeline(activeFilters.timeline)
    }, [activeFilters.timeline])

    const params = useMemo(() => {
        const {
            basis,
            comparisonPeriod,
            display,
            analysisType,
            applySquareFootage,
            applyWeatherNormalization,
        } = reportSettings

        const params: BenchmarkingRequestParams = {
            ...filtersParams,
            type: display,
            trendType: analysisType,
            applyWeatherNormalization,
            applySquareFootage: false, // Default to false, will be updated if area setting is displayed
            includeEmissions: false, // Default to false, will be updated if emissions basis is selected
        }

        if (analysisType !== 'trend') {
            params.comparisonPeriod = comparisonPeriod
        }

        if (basis === 'emission') {
            params.includeEmissions = true
        }

        if (shouldDisplayAreaSetting(reportSettings)) {
            params.applySquareFootage = applySquareFootage
        }

        return params
    }, [filtersParams, reportSettings])

    const calculationMethodOffset = useMemo(() => {
        if (!isOpen) {
            return toggleWidth
        }

        return 16
    }, [isOpen, toggleWidth])

    useEffect(() => {
        setSlot(<BenchmarkingActions filters={params} />)
        return () => {
            setSlot(null)
        }
    }, [params, setSlot])

    return (
        <DrawerWrapper>
            <BenchmarkingDrawer
                filters={params}
                reportSettings={reportSettings}
                setReportSettings={setReportSettings}
                baselineYearTimeline={baselineYearTimeline}
                previousYearTimeline={previousYearTimeline}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                toggleButtonRef={toggleButtonRef}
            />
            <BenchmarkingReport
                filters={params}
                reportSettings={reportSettings}
                baselineYearTimeline={baselineYearTimeline}
                previousYearTimeline={previousYearTimeline}
                isOpen={isOpen}
                calculationMethodOffset={calculationMethodOffset}
            />
        </DrawerWrapper>
    )
}
